import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import {
  Bell,
  Workflow,
  Bot,
  User,
  LogOut,
  Zap,
  LayoutDashboard,
  Coins,
  Mail,
  Megaphone,
  Moon,
  Sun,
  Settings,
  CheckCircle2,
  AlertCircle,
  Info,
  ShoppingCart,
  Puzzle,
  BarChart3,
  Trophy,
  DollarSign,
  FileText,
  ChevronLeft,
  ChevronRight,
  Code2,
  Calendar,
  Gauge,
  TrendingUp,
  Shield,
  CreditCard,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Estado para notificaciones
  const [notifications, setNotifications] = useState([
    {
      id: '1',
      type: 'success',
      title: 'Workflow ejecutado',
      message: 'Envío de Ofertas completado exitosamente',
      time: '5 min',
      read: false,
    },
    {
      id: '2',
      type: 'warning',
      title: 'Certificación pendiente',
      message: 'Activo "Vehículos Corporativos" requiere aprobación',
      time: '1 hora',
      read: false,
    },
    {
      id: '3',
      type: 'info',
      title: 'Nuevo agente IA',
      message: 'Asistente de Ventas configurado correctamente',
      time: '2 horas',
      read: true,
    },
  ]);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada correctamente');
    navigate('/login');
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
    toast.success(isDarkMode ? 'Modo claro activado' : 'Modo oscuro activado');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = (notification: any) => {
    // Marcar como leída
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
    
    // Navegar según el tipo
    if (notification.type === 'success') {
      navigate('/dashboard/workflows');
    } else if (notification.type === 'warning') {
      navigate('/dashboard/assets');
    } else {
      navigate('/dashboard/agents');
    }
    
    toast.info(`Abriendo: ${notification.title}`);
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    toast.success('Todas las notificaciones marcadas como leídas');
  };

  const viewAllNotifications = () => {
    navigate('/dashboard/notifications');
    toast.info('Abriendo todas las notificaciones');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/dashboard/workflows', label: 'Workflows', icon: Workflow },
    { path: '/dashboard/templates', label: 'Templates', icon: FileText, badge: 'NEW' },
    { path: '/dashboard/marketplace', label: 'Marketplace', icon: ShoppingCart, badge: 'NEW' },
    { path: '/dashboard/integrations', label: 'Integraciones', icon: Puzzle, badge: 'NEW' },
    { path: '/dashboard/analytics', label: 'Analytics', icon: BarChart3, badge: 'NEW' },
    { path: '/dashboard/agents', label: 'AI Agents', icon: Bot },
    { path: '/dashboard/achievements', label: 'Logros', icon: Trophy, badge: 'NEW' },
    { path: '/dashboard/payroll', label: 'Nómina', icon: DollarSign },
    { path: '/dashboard/assets', label: 'Activos', icon: Coins },
    { path: '/dashboard/mail', label: 'Correos', icon: Mail },
    { path: '/dashboard/offers', label: 'Ofertas', icon: Megaphone },
  ];

  const toolsItems = [
    { path: '/dashboard/scheduler', label: 'Programador', icon: Calendar, badge: undefined as string | undefined },
    { path: '/dashboard/data-transformer', label: 'Transformador', icon: Code2, badge: undefined as string | undefined },
    { path: '/dashboard/performance', label: 'Performance', icon: Gauge, badge: undefined as string | undefined },
    { path: '/dashboard/roi', label: 'ROI Dashboard', icon: TrendingUp, badge: 'NEW' as string | undefined },
    { path: '/dashboard/compliance', label: 'Compliance', icon: Shield, badge: 'NEW' as string | undefined },
    { path: '/dashboard/billing', label: 'Facturación', icon: CreditCard, badge: undefined as string | undefined },
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col transition-all duration-300 z-40 ${
          isSidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {!isSidebarCollapsed && (
            <Link to="/dashboard" className="flex items-center gap-2.5">
              <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-9 w-9 object-contain" />
              <span className="text-xl font-semibold tracking-tight">ZAPHUB</span>
            </Link>
          )}
          {isSidebarCollapsed && (
            <Link to="/dashboard" className="flex items-center justify-center w-full">
              <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-9 w-9 object-contain" />
            </Link>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <div className="space-y-1">
            {navItems.map(({ path, label, icon: Icon, badge }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                  isActive(path)
                    ? 'bg-primary text-white font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                title={isSidebarCollapsed ? label : undefined}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!isSidebarCollapsed && (
                  <>
                    <span className="flex-1">{label}</span>
                    {badge && <Badge variant="secondary" className="text-xs">{badge}</Badge>}
                  </>
                )}
              </Link>
            ))}
          </div>

          {/* Tools Section */}
          {!isSidebarCollapsed && (
            <div className="mt-6">
              <p className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Herramientas
              </p>
            </div>
          )}
          <div className="space-y-1 mt-2">
            {toolsItems.map(({ path, label, icon: Icon, badge }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                  isActive(path)
                    ? 'bg-primary text-white font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                title={isSidebarCollapsed ? label : undefined}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!isSidebarCollapsed && (
                  <>
                    <span className="flex-1">{label}</span>
                    {badge && <Badge variant="secondary" className="text-xs">{badge}</Badge>}
                  </>
                )}
              </Link>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-border p-3">
          {!isSidebarCollapsed ? (
            <div className="flex items-center gap-2 px-1">
              <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-8 w-8 object-contain" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">ZAPHUB</span>
                <span className="text-[10px] text-muted-foreground">v2.1.0 Enterprise</span>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-8 w-8 object-contain" />
            </div>
          )}
        </div>

        {/* Collapse Button */}
        <div className="p-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="w-full justify-center"
          >
            {isSidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4 mr-2" />
                <span className="text-xs">Colapsar</span>
              </>
            )}
          </Button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="h-full flex items-center justify-between px-6">
            {/* Breadcrumb or Page Title */}
            <div>
              <h1 className="text-lg font-semibold">
                {location.pathname === '/dashboard' ? 'Dashboard' : 
                 location.pathname.split('/').pop()?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'ZAPHUB'}
              </h1>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* Dark Mode Toggle */}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleDarkMode}
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <Badge 
                        variant="destructive" 
                        className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                      >
                        {unreadCount}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>Notificaciones</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-auto py-0 px-2 text-xs"
                      onClick={markAllAsRead}
                    >
                      Marcar todas como leídas
                    </Button>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifications.map((notification) => (
                    <DropdownMenuItem 
                      key={notification.id}
                      className="flex items-start gap-3 p-3 cursor-pointer"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0 ${
                        notification.type === 'success' ? 'bg-green-500/10 text-green-600' :
                        notification.type === 'warning' ? 'bg-orange-500/10 text-orange-600' :
                        'bg-blue-500/10 text-blue-600'
                      }`}>
                        {notification.type === 'success' && <CheckCircle2 className="h-4 w-4" />}
                        {notification.type === 'warning' && <AlertCircle className="h-4 w-4" />}
                        {notification.type === 'info' && <Info className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{notification.title}</p>
                          {!notification.read && (
                            <span className="flex h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{notification.message}</p>
                        <p className="text-xs text-muted-foreground">{notification.time} ago</p>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="justify-center text-sm text-primary" onClick={viewAllNotifications}>
                    Ver todas las notificaciones
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2.5 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-white text-sm font-medium">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:flex flex-col items-start">
                      <span className="text-sm font-medium">{user?.name}</span>
                      <span className="text-xs text-muted-foreground">{user?.role}</span>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => toast.info('Abriendo configuración...')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Configuración
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t border-border py-4 px-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <p>© 2025 ZAPHUB 2.1. Sistema privado de automatización empresarial.</p>
            <p>v2.1.0 · claude-sonnet-4-6</p>
          </div>
        </footer>
      </div>
    </div>
  );
}